import { useEffect, useMemo, useState } from 'react';
import {
  fetchWorkloadDetail,
  unplannedOf,
  type MemberCounts,
  type StageDef,
  type UnplannedBreakdown,
  type WorkloadDetail,
  type WorkloadDetailMember,
  type WorkloadResponse,
} from '../../../api/work';
import { EvidencePopover, type ExtraSection } from './EvidencePopover';
import { Legend, StackedRow, type Badge, type Segment } from './StackedRow';
import './workload-panels.css';

/**
 * The Workload page's diagrams.
 *
 * **Reported as "the workload page consisted a few carefully designed
 * diagrams but now it only shows a matrix".** It did. The predecessor drew
 * six stacked-bar panels, one row per member, whose bars you hovered to
 * list the tickets and commits behind the number; the port read
 * `metric_values`, which stores scalars, so there were no segments to stack
 * and no evidence to show.
 *
 * Three panels here, and they are the three the mirror can answer today:
 *
 * * **Open workload by lane** — where each person's unresolved work sits
 * * **Delivery** — what they resolved in the window
 * * **Activity** — eight segments, including the own-versus-other splits
 *   that were one undifferentiated number before
 *
 * ...plus **one pipeline panel per lane that declares stages**, which is
 * where the predecessor's TD delivery stages and SUP pipeline went. Those
 * count transitions into *named statuses*, and the names are one site's
 * workflow vocabulary — this mirror records `CLEAR FOR PREP` and
 * `READY FOR DELIVERY` in the delivery project, `INITIAL CHECK` and
 * `TO HANDOVER` in support — so they live in
 * `work.metrics.workflow.stages`, keyed by lane. The predecessor hard-coded
 * three panels against its own status names, which gave any other
 * deployment three panels of zeroes; here a lane with nothing declared gets
 * no panel at all.
 *
 * ...and **planning failures**: tickets created after a planning cycle began
 * and due before the next one — work there was no opportunity to plan. This
 * panel is not computed here. `unplanned_tasks_count` in `work.metrics` owns
 * the rule, its cadence is that metric's parameters, and the page reads the
 * value and breakdown the metric stored — so the panel, the matrix below and
 * the briefings cannot disagree about who had an unplannable week. It could
 * not exist before machinai 0.28.0: nothing could set a metric parameter,
 * and the rule's `projects` defaults to none.
 *
 * Its window is the metric's (`window_days`, 30 on this deployment), not the
 * chips above, and the panel says so rather than silently ignoring them.
 *
 * **Every panel scales to its own busiest row**, so a row is comparable
 * within a panel and not across panels — commits and open tickets are not
 * the same kind of quantity and a shared scale would imply they were.
 *
 * **The matrix stays, below.** It carries fourteen dimensions these panels
 * do not — cycle time, reaction time, the project shares — and deleting it
 * to restore the diagrams would be trading one loss for another.
 */

const WINDOWS = [7, 30, 90];

/** The predecessor's palette and segment order, which the legend repeats. */
const ACTIVITY: Array<{ key: keyof MemberCounts; label: string; color: string }> = [
  { key: 'commits', label: 'commits', color: '#6d7bef' },
  { key: 'prs_merged', label: 'PRs merged', color: '#9ee7bf' },
  { key: 'prs_reviewed', label: 'PRs reviewed', color: '#c488ef' },
  { key: 'actions_own', label: 'Jira actions (own)', color: '#f0b449' },
  { key: 'actions_other', label: "Jira actions (others')", color: '#f7d97e' },
  { key: 'comments_own', label: 'comments (own)', color: '#d9644a' },
  { key: 'comments_other', label: "comments (others')", color: '#f0a394' },
  { key: 'reported', label: 'tickets reported', color: '#46d4d4' },
];

const DELIVERED_COLOR = '#3dd68c';

/**
 * For a lane `/detail` does not describe — a project no configured lane
 * claims. A lane's own colour, label and order come from `/detail`'s
 * `lanes`, which is `work.trends.lanes` through `work.trends`' parser; they
 * were compiled in here for wingd's four keys until extensions 0.44.0, so
 * any other deployment's lanes drew grey and titled with raw keys.
 */
const UNMAPPED_COLOR = '#555970';

/** The tone early stages are mixed toward — the panel's own surface. */
const STAGE_FLOOR = { r: 0x3a, g: 0x3e, b: 0x52 };

/**
 * A stage's colour: its own if configured, else a ramp off the lane's.
 *
 * **The ramp carries the pipeline's direction.** Early stages sit close to
 * the panel surface and each one moves toward the lane's full colour, so a
 * bar that is mostly dim is work sitting early in the pipeline and a bar
 * that is mostly bright is work that reached the end. With one flat colour
 * per lane the segments would be distinguishable only by position, which is
 * exactly the information a reader is trying to get from the widths.
 *
 * A configured colour always wins: an owner who names the shades means them.
 */
function stageColor(
  base: string,
  index: number,
  total: number,
  explicit: string | null,
): string {
  if (explicit) return explicit;
  const parsed = /^#?([0-9a-fA-F]{6})$/.exec(base);
  const hex = parsed?.[1];
  if (hex === undefined) return base;
  const n = Number.parseInt(hex, 16);
  // The last stage is the lane colour itself; the first sits at 0.35 of the
  // way from the floor. Not 0: a first segment indistinguishable from the
  // panel background reads as empty space rather than as a quantity.
  const t = total <= 1 ? 1 : 0.35 + (0.65 * index) / (total - 1);
  const mix = (channel: number, floor: number) =>
    Math.round(floor + (channel - floor) * t);
  const r = mix((n >> 16) & 0xff, STAGE_FLOOR.r);
  const g = mix((n >> 8) & 0xff, STAGE_FLOOR.g);
  const b = mix(n & 0xff, STAGE_FLOOR.b);
  return `rgb(${r}, ${g}, ${b})`;
}

/** A lane's segments for one member, in configured stage order. */
function stageSegments(
  base: string,
  defs: StageDef[],
  counts: Record<string, number> | undefined,
): Segment[] {
  return defs.map((d, i) => ({
    label: d.label,
    // `?? 0` rather than skipping the segment: the route sends every
    // configured stage, and a row that dropped its empty ones would put a
    // different colour under the same x-position as its neighbours.
    value: counts?.[d.label] ?? 0,
    color: stageColor(base, i, defs.length, d.color),
  }));
}

/**
 * Badges derived from the counts alone.
 *
 * The predecessor had more — workflow drift, a hardware-labelling warning —
 * computed from fields this endpoint does not carry. These are the two the
 * data supports, and both are observations a reader would otherwise have to
 * do arithmetic to notice.
 */
function badgesFor(c: MemberCounts): Badge[] {
  const out: Badge[] = [];
  const actions = c.actions_own + c.actions_other;
  if (actions >= 20 && c.actions_other > c.actions_own * 3) {
    out.push({
      label: 'coord',
      tone: 'info',
      title:
        `${c.actions_other} of ${actions} Jira actions were on other ` +
        "people's tickets — coordination rather than execution",
    });
  }
  if (c.prs_merged >= 5 && c.prs_merged_deep === 0) {
    out.push({
      label: 'shallow',
      tone: 'muted',
      title:
        `${c.prs_merged} PRs merged and none open longer than 24 hours — ` +
        'cadence without the shipping signal that survives it',
    });
  }
  return out;
}

function PanelShell({
  title,
  hint,
  segments,
  children,
}: {
  title: string;
  hint: string;
  segments: Segment[];
  children: React.ReactNode;
}) {
  return (
    <section className="wlp">
      <header className="wlp__head">
        <div>
          <span className="wlp__title">{title}</span>
          <span className="wlp__hint">{hint}</span>
        </div>
        <Legend segments={segments} />
      </header>
      <div className="wlp__rows">{children}</div>
    </section>
  );
}

/** The metric that owns the planning-failure rule. */
const UNPLANNED = 'unplanned_tasks_count';

function shortDate(ms: number): string {
  return new Date(ms).toLocaleDateString([], { month: 'short', day: 'numeric' });
}

/** The popover's rows for one member's unplanned tickets. */
function unplannedSection(b: UnplannedBreakdown): ExtraSection {
  return {
    label: 'Planning failures',
    rows: b.tickets.map((t) => ({
      key: t.issue_key,
      project: t.project,
      preview: `due ${shortDate(t.due_at_ms)}`,
      created_at_ms: t.created_at_ms,
    })),
    note:
      b.count > b.tickets.length
        ? `showing ${b.tickets.length} of ${b.count} — the metric stores 30`
        : undefined,
  };
}

interface Props {
  /**
   * `/api/work/workload`, which the page has already fetched for its matrix.
   * The planning-failures panel reads `unplanned_tasks_count` from it; passed
   * down rather than fetched again. Absent, that panel is not drawn.
   */
  workload?: WorkloadResponse | null;
}

export function WorkloadPanels({ workload }: Props = {}) {
  const [days, setDays] = useState(30);
  const [data, setData] = useState<WorkloadDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    setData(null);
    setError(null);
    fetchWorkloadDetail(days)
      .then((d) => live && setData(d))
      .catch((e: unknown) =>
        live && setError(e instanceof Error ? e.message : String(e)),
      );
    return () => {
      live = false;
    };
  }, [days]);

  const members = useMemo(
    () => (data?.members ?? []).filter((m) => m.counts !== null),
    [data],
  );

  /** The configured lanes, by category: colour, label and, by position, order. */
  const laneMeta = useMemo(
    () => new Map((data?.lanes ?? []).map((l) => [l.category, l])),
    [data],
  );
  const order = useMemo(() => (data?.lanes ?? []).map((l) => l.category), [data]);
  const colorOf = (lane: string) => laneMeta.get(lane)?.color ?? UNMAPPED_COLOR;
  const labelOf = (lane: string) => laneMeta.get(lane)?.label ?? lane;

  /** Lanes in the configured order, plus anything else present. */
  const lanes = useMemo(() => {
    const seen = new Set<string>();
    for (const m of members) {
      for (const lane of Object.keys(m.open_by_lane ?? {})) seen.add(lane);
    }
    const known = order.filter((l) => seen.has(l));
    return [...known, ...[...seen].filter((l) => !known.includes(l)).sort()];
  }, [members, order]);

  /**
   * Lanes with stages, in the lane order the rest of the page uses.
   *
   * Ordered from the known list first so the pipeline panels appear in the
   * same sequence as the "open workload by lane" segments — a reader moving
   * down the page should not have to re-learn which lane is which.
   */
  const stageLanes = useMemo(() => {
    const declared = Object.keys(data?.stages ?? {});
    const known = order.filter((l) => declared.includes(l));
    return [...known, ...declared.filter((l) => !known.includes(l)).sort()];
  }, [data, order]);

  /** Each member's planning-failure breakdown, by roster name. */
  const unplanned = useMemo(() => {
    const out = new Map<string, { breakdown: UnplannedBreakdown | null; reason: string | null }>();
    for (const m of workload?.members ?? []) {
      const cell = m.metrics[UNPLANNED];
      out.set(m.person, { breakdown: unplannedOf(cell), reason: cell?.reason ?? null });
    }
    return out;
  }, [workload]);

  const openSegments = (m: WorkloadDetailMember): Segment[] => [
    ...lanes.map((lane) => ({
      label: lane,
      value: m.open_by_lane?.[lane] ?? 0,
      color: colorOf(lane),
    })),
    {
      label: 'no lane',
      value: m.open_unmapped ?? 0,
      color: UNMAPPED_COLOR,
    },
  ];

  const activitySegments = (c: MemberCounts): Segment[] =>
    ACTIVITY.map((s) => ({
      label: s.label,
      value: Number(c[s.key] ?? 0),
      color: s.color,
    }));

  const sum = (ss: Segment[]) => ss.reduce((a, s) => a + s.value, 0);
  const maxOpen = Math.max(1, ...members.map((m) => sum(openSegments(m))));
  const maxDelivered = Math.max(
    1,
    ...members.map((m) => m.counts?.resolved ?? 0),
  );
  const maxActivity = Math.max(
    1,
    ...members.map((m) => sum(activitySegments(m.counts as MemberCounts))),
  );

  if (error) {
    return (
      <p className="wlp__error">
        {error}
        <br />
        These panels read the work mirror through <code>work.collect</code>.
      </p>
    );
  }
  if (data === null) return <p className="wlp__dim">Loading…</p>;
  if (members.length === 0) {
    return (
      <p className="wlp__dim">
        No team members. The roster is <code>work.metrics.team.members</code>,
        and the mirror is queried by each member's Jira and GitHub aliases —
        so an unconfigured roster has nobody to report rather than everybody.
      </p>
    );
  }

  const laneSegmentsForLegend: Segment[] = [
    ...lanes.map((l) => ({
      label: l,
      value: 0,
      color: colorOf(l),
    })),
    ...(members.some((m) => (m.open_unmapped ?? 0) > 0)
      ? [{ label: 'no lane', value: 0, color: UNMAPPED_COLOR }]
      : []),
  ];

  return (
    <div className="wlps">
      <div className="wlps__bar">
        <div className="wlp__chips" role="group" aria-label="Window">
          {WINDOWS.map((w) => (
            <button
              key={w}
              type="button"
              className={`wlp__chip${days === w ? ' is-active' : ''}`}
              onClick={() => setDays(w)}
            >
              {w}d
            </button>
          ))}
        </div>
        <span className="wlp__dim">
          {members.length} members · click a row for the tickets and commits
          behind its bar
          {data.lanes_configured === 0 &&
            ' · no lanes configured, so open work is unsegmented'}
        </span>
      </div>

      <PanelShell
        title="Open workload by lane"
        hint={
          // Not "unresolved": open is a status rule, and saying which
          // statuses close a ticket is the point — see `_open_clause`.
          `open work, now — not a window · open = not in ${
            (data.open_excludes ?? []).join(', ') || 'the terminal set'
          }`
        }
        segments={laneSegmentsForLegend}
      >
        {members.map((m) => (
          <StackedRow
            key={m.person}
            name={m.person}
            role={m.role}
            segments={openSegments(m)}
            scaleMax={maxOpen}
            badges={badgesFor(m.counts as MemberCounts)}
            onSelect={() => setSelected(m.person)}
            selected={selected === m.person}
          />
        ))}
      </PanelShell>

      <PanelShell
        title="Delivery"
        hint={`Jira tickets resolved in the last ${days} days`}
        segments={[
          { label: 'resolved', value: 0, color: DELIVERED_COLOR },
        ]}
      >
        {members.map((m) => (
          <StackedRow
            key={m.person}
            name={m.person}
            role={m.role}
            segments={[
              {
                label: 'resolved',
                value: m.counts?.resolved ?? 0,
                color: DELIVERED_COLOR,
              },
            ]}
            scaleMax={maxDelivered}
            onSelect={() => setSelected(m.person)}
            selected={selected === m.person}
          />
        ))}
      </PanelShell>

      {stageLanes.map((lane) => {
        const defs = data.stages?.[lane] ?? [];
        // Each panel scales to its own busiest row, like the others: a
        // delivery lane's six stages and a support lane's five are not the
        // same quantity and a shared scale would imply they were.
        const scaleMax = Math.max(
          1,
          ...members.map((m) =>
            sum(stageSegments(colorOf(lane), defs, m.stage_counts?.[lane])),
          ),
        );
        return (
          <PanelShell
            key={`stages-${lane}`}
            title={`${labelOf(lane)} pipeline`}
            hint={`status transitions in the last ${days} days · ${defs
              .map((d) => d.label)
              .join(' → ')}`}
            segments={defs.map((d, i) => ({
              label: d.label,
              value: 0,
              color: stageColor(colorOf(lane), i, defs.length, d.color),
            }))}
          >
            {members.map((m) => (
              <StackedRow
                key={m.person}
                name={m.person}
                role={m.role}
                segments={stageSegments(colorOf(lane), defs, m.stage_counts?.[lane])}
                scaleMax={scaleMax}
                onSelect={() => setSelected(m.person)}
                selected={selected === m.person}
              />
            ))}
          </PanelShell>
        );
      })}

      {workload && (() => {
        const rows = members.map((m) => ({ m, u: unplanned.get(m.person) }));
        const first = rows.find((r) => r.u?.breakdown)?.u?.breakdown ?? null;
        const ageMin = Math.round(workload.age_ms / 60_000);
        if (first === null) {
          // Declined for everyone, or not computed yet. One line rather
          // than eleven grey rows: the reason is the same for all of them,
          // and it is a statement about configuration, not about people.
          const reasons = [
            ...new Set(
              rows
                .map((r) => r.u?.reason)
                .filter((r): r is string => typeof r === 'string' && r !== ''),
            ),
          ];
          return (
            <section className="wlp">
              <header className="wlp__head">
                <div>
                  <span className="wlp__title">Planning failures</span>
                  <span className="wlp__hint">not computed</span>
                </div>
              </header>
              <p className="wlp__dim">
                <code>{UNPLANNED}</code>
                {reasons.length > 0 ? `: ${reasons.join('; ')}` : ' has no value yet'}
                . It judges only the projects named in{' '}
                <code>work.metrics.params.{UNPLANNED}.projects</code>.
              </p>
            </section>
          );
        }
        const projects = first.projects;
        // A project is drawn in its lane's colour, so the same colour means
        // the same lane on every panel.
        const projectColor = (project: string) =>
          colorOf(data.project_lanes?.[project] ?? '');
        const segments = (b: UnplannedBreakdown | null): Segment[] =>
          projects.map((project) => ({
            label: `${project} unplanned`,
            // From `by_project`, which counts every ticket — never from the
            // 30-row `tickets` list, which would stop short.
            value: b?.by_project[project] ?? 0,
            color: projectColor(project),
          }));
        const scaleMax = Math.max(1, ...rows.map((r) => r.u?.breakdown?.count ?? 0));
        return (
          <PanelShell
            title="Planning failures"
            hint={
              `created after the planning cycle began, due before the next · ` +
              `last ${first.window_days}d as computed by work.metrics ` +
              `${ageMin} min ago — the window above does not apply`
            }
            segments={segments(null)}
          >
            {rows.map(({ m, u }) => {
              const b = u?.breakdown ?? null;
              return (
                <StackedRow
                  key={m.person}
                  name={m.person}
                  role={m.role}
                  segments={segments(b)}
                  scaleMax={scaleMax}
                  subtitle={
                    b === null
                      ? (u?.reason ?? 'not computed')
                      : b.undated > 0
                        ? `${b.undated} undated — no due date, so not judged`
                        : undefined
                  }
                  onSelect={() => setSelected(m.person)}
                  selected={selected === m.person}
                />
              );
            })}
          </PanelShell>
        );
      })()}

      <PanelShell
        title="Activity"
        hint={`what they did in the last ${days} days, by kind`}
        segments={activitySegments({
          commits: 0,
          prs_merged: 0,
          prs_merged_deep: 0,
          prs_reviewed: 0,
          actions_own: 0,
          actions_other: 0,
          comments_own: 0,
          comments_other: 0,
          open: 0,
          resolved: 0,
          reported: 0,
        })}
      >
        {members.map((m) => {
          const c = m.counts as MemberCounts;
          return (
            <StackedRow
              key={m.person}
              name={m.person}
              role={m.role}
              segments={activitySegments(c)}
              scaleMax={maxActivity}
              subtitle={
                c.prs_merged > 0
                  ? `${c.prs_merged_deep}/${c.prs_merged} PRs over 24h`
                  : undefined
              }
              badges={badgesFor(c)}
              onSelect={() => setSelected(m.person)}
              selected={selected === m.person}
            />
          );
        })}
      </PanelShell>

      {selected && (
        <EvidencePopover
          person={selected}
          days={days}
          onClose={() => setSelected(null)}
          extra={(() => {
            const b = unplanned.get(selected)?.breakdown;
            return b ? [unplannedSection(b)] : undefined;
          })()}
        />
      )}
    </div>
  );
}
